<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useNotificationsStore } from '@/stores/notificationsStore'

/**
 * Header bell — unread-count badge + click-to-jump-to-inbox.
 *
 * Uses PV `<OverlayBadge>` in its canonical shape: wrapping the
 * ICON directly (not a Button) — the badge sits cleanly at the
 * icon's top-right with no double-chrome collision against the
 * Button's rounded background. Plain `<button>` element provides
 * click + a11y + focus chrome.
 *
 * Polls the bell-count every 60s (paused when tab hidden — page-
 * visibility API). Click navigates to `/notifications`.
 */
defineOptions({ name: 'NotificationsBell' })

const { t } = useI18n()
const router = useRouter()
const store = useNotificationsStore()
const { unreadCount, unreadBadgeLabel } = storeToRefs(store)

onMounted(() => {
  store.startPolling()
})

onBeforeUnmount(() => {
  store.stopPolling()
})

const open = (): void => {
  void router.push({ name: 'Notifications' })
}
</script>

<template>
  <!--
    PV's Overlay shape: wrap the icon directly. The badge defaults
    (Aura preset: `min-width: 1.5rem, height: 1.5rem, padding:
    0 0.5rem, font-size: 0.75rem`) are sized for a 2rem icon — way
    too chunky next to a header-tier 16px icon. We shrink the
    badge AT THE INSTANCE LEVEL via the `.mini-badge` class +
    deep-selector CSS below (doesn't touch other badges app-wide).
  -->
  <button
    type="button"
    class="inline-flex items-center justify-center w-9 h-9 rounded-full text-color-secondary hover:bg-surface-hover hover:text-color transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    :aria-label="t('header.notifications', { count: unreadCount })"
    @click="open"
  >
    <OverlayBadge
      v-if="unreadCount > 0"
      :value="unreadBadgeLabel ?? ''"
      severity="info"
      class="mini-badge"
    >
      <i class="pi pi-bell text-base" />
    </OverlayBadge>
    <i v-else class="pi pi-bell text-base" />
  </button>
</template>

<style scoped>
/*
 * Mini variant of OverlayBadge for header-chrome icons. Targets the
 * inner `.p-badge` via `:deep()` (the badge sits inside the
 * OverlayBadge wrapper, owned by PV's component shadow).
 *
 * Tokens here are roughly half of PV Aura's default badge tokens:
 *   default: min-width 1.5rem, height 1.5rem, padding 0 0.5rem, font 0.75rem
 *   mini:    min-width 1rem,   height 1rem,   padding 0 0.25rem, font 0.625rem
 *
 * Sits cleanly at the corner of a 16-20px icon. For multi-digit
 * counts (10+), `min-width` lets it stretch to a tight pill.
 */
.mini-badge :deep(.p-badge) {
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.625rem;
  line-height: 1rem;
}
</style>
